import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Mail,
  MessageSquare,
  RefreshCw,
  ShieldAlert,
  Layers,
} from 'lucide-react';
import { useState } from 'react';

import { Badge, Spinner } from '@terra/ui';

import { api } from '../lib/api';

const WINDOWS = [
  { hours: 1, label: 'Last hour' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 168, label: 'Last 7 days' },
  { hours: 720, label: 'Last 30 days' },
];

export function NotificationDashboardPage() {
  const [windowHours, setWindowHours] = useState(24);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['notification-dashboard', windowHours],
    queryFn: () => api.notifications.dashboard(windowHours),
    // Ops screens go stale fast; 30s keeps it live without hammering the DB.
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="grid min-h-[400px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!data) return null;

  const sms = data.providers.sms;
  const email = data.providers.email;
  const totalFailed = data.delivery.reduce((n, d) => n + d.failed, 0);
  const missingTemplates = sms.dltTemplates.filter((t) => !t.configured);

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-ink text-xl font-black">Notification Delivery</h1>
          <p className="text-ink-subtle mt-0.5 text-[13px]">
            Updated {new Date(data.generatedAt).toLocaleTimeString()} · refreshes every 30s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="border-border/60 bg-surface text-ink rounded-lg border px-3 py-1.5 text-[13px] font-semibold"
            value={windowHours}
            onChange={(e) => setWindowHours(Number(e.target.value))}
          >
            {WINDOWS.map((w) => (
              <option key={w.hours} value={w.hours}>
                {w.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => void refetch()}
            className="border-border/60 text-ink hover:bg-surface-subtle flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-bold transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Things that need attention, if any. Nothing renders when all is well —
          a banner that is always present stops being read. */}
      {(missingTemplates.length > 0 ||
        !sms.configured ||
        !email.configured ||
        !data.queues.available) && (
        <div className="space-y-2">
          {missingTemplates.length > 0 && (
            <Alert
              tone="warning"
              title={`${missingTemplates.length} SMS purpose${missingTemplates.length === 1 ? '' : 's'} cannot send`}
              detail={`No DLT template configured for: ${missingTemplates.map((t) => t.label).join(', ')}. Those messages report themselves unavailable instead of being dropped by carriers. Add the approved IDs in Setup → Configurations → SMS.`}
            />
          )}
          {!sms.configured && (
            <Alert
              tone="warning"
              title="SMS gateway is not configured"
              detail="SMS falls back to console logging — nothing reaches a handset."
            />
          )}
          {!email.configured && (
            <Alert
              tone="warning"
              title="SMTP is not configured"
              detail="Email falls back to console logging — nothing reaches an inbox."
            />
          )}
          {sms.configured && !sms.endpointIsHttps && (
            <Alert
              tone="warning"
              title="SMS gateway is using plain HTTP"
              detail="The API key is transmitted unencrypted on every send. Ask the provider for an HTTPS endpoint."
            />
          )}
          {!data.queues.available && (
            <Alert
              tone="neutral"
              title="Queue depth unavailable"
              detail={data.queues.reason ?? ''}
            />
          )}
        </div>
      )}

      {/* Pipeline health — the at-a-glance answer for whoever is on call */}
      <Section icon={<Activity className="h-4 w-4" />} title="Queue health">
        {data.pipeline.sampleSize === 0 ? (
          <p className="text-ink-subtle p-5 text-[13px] font-medium">
            Nothing has been delivered in this window, so there is no latency to report.
          </p>
        ) : (
          <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              label="Queue delay · p50"
              value={formatDelay(data.pipeline.queueDelayMs.p50)}
              hint="Typical wait before a worker picks a message up"
            />
            <Kpi
              label="Queue delay · p95"
              value={formatDelay(data.pipeline.queueDelayMs.p95)}
              // The number to watch: an average would hide a tail of messages
              // that waited minutes while most waited milliseconds.
              tone={delayTone(data.pipeline.queueDelayMs.p95)}
              hint="What the unlucky 5% experienced"
            />
            <Kpi
              label="Retry rate"
              value={
                data.pipeline.retry.retryRate === null ? '—' : `${data.pipeline.retry.retryRate}%`
              }
              tone={
                data.pipeline.retry.retryRate === null || data.pipeline.retry.retryRate < 2
                  ? 'neutral'
                  : data.pipeline.retry.retryRate < 10
                    ? 'warning'
                    : 'danger'
              }
              hint={`${data.pipeline.retry.messagesRetried} of ${data.pipeline.sampleSize} needed more than one attempt`}
            />
            <Kpi
              label="Failed deliveries"
              value={String(data.delivery.reduce((n, d) => n + d.failed, 0))}
              tone={data.delivery.reduce((n, d) => n + d.failed, 0) > 0 ? 'danger' : 'neutral'}
              hint={`In the last ${data.window.hours}h`}
            />
          </div>
        )}

        {/* Which channel is driving the retries — a platform-wide rate says
            something is wrong, not what. */}
        {data.pipeline.byChannel.some((c) => c.retried > 0) && (
          <div className="border-border/60 border-t px-5 py-3">
            <p className="text-ink-subtle text-[12px] font-medium">
              Retries by channel:{' '}
              {data.pipeline.byChannel
                .filter((c) => c.retried > 0)
                .map((c) => `${c.channel} ${c.retryRate}% (${c.retried}/${c.attempted})`)
                .join(' · ')}
            </p>
          </div>
        )}
      </Section>

      {/* Delivery by channel — the headline answer */}
      <Section icon={<Activity className="h-4 w-4" />} title="Delivery by channel">
        <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
          {data.delivery.map((d) => (
            <div key={d.channel} className="border-border/60 rounded-xl border p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-ink text-[13px] font-bold">{d.channel}</span>
                {d.deliveryRate === null ? (
                  <Badge tone="neutral">no traffic</Badge>
                ) : (
                  <Badge
                    tone={
                      d.deliveryRate >= 95 ? 'success' : d.deliveryRate >= 80 ? 'warning' : 'danger'
                    }
                  >
                    {d.deliveryRate}% delivered
                  </Badge>
                )}
              </div>
              <p className="text-ink text-2xl font-black">{d.total}</p>
              <p className="text-ink-subtle mt-1 text-[12px] font-medium">
                {d.sent} sent · {d.queued} queued ·{' '}
                <span className={d.failed > 0 ? 'font-bold text-red-500' : ''}>
                  {d.failed} failed
                </span>
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Provider health */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Section icon={<MessageSquare className="h-4 w-4" />} title="SMS provider">
          <dl className="space-y-2 p-5 text-[13px]">
            <Row label="Provider" value={sms.provider} />
            <Row label="Sender ID" value={sms.senderId ?? '—'} />
            <Row
              label="Configured"
              value={sms.configured ? 'yes' : 'no'}
              tone={sms.configured ? 'ok' : 'bad'}
            />
            <Row
              label="Transport"
              value={sms.endpointIsHttps ? 'HTTPS' : 'HTTP (unencrypted)'}
              tone={sms.endpointIsHttps ? 'ok' : 'warn'}
            />
            <Row label="Sent in window" value={String(sms.sentInWindow)} />
            <Row
              label="Failed in window"
              value={String(sms.failedInWindow)}
              tone={sms.failedInWindow > 0 ? 'bad' : 'ok'}
            />
            <Row label="Last success" value={fmt(sms.lastSuccessAt)} />
            {sms.lastError && <Row label="Last error" value={sms.lastError} tone="bad" />}
          </dl>
          <div className="border-border/60 border-t px-5 py-4">
            <p className="text-ink mb-2 text-[12px] font-bold uppercase tracking-wider">
              DLT templates
            </p>
            <div className="space-y-1.5">
              {sms.dltTemplates.map((t) => (
                <div key={t.purpose} className="flex items-center justify-between text-[13px]">
                  <span className="text-ink-subtle capitalize">{t.label}</span>
                  <Badge tone={t.configured ? 'success' : 'warning'}>
                    {t.configured ? 'configured' : 'missing'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section icon={<Mail className="h-4 w-4" />} title="Email provider">
          <dl className="space-y-2 p-5 text-[13px]">
            <Row label="Provider" value={email.provider} />
            <Row label="Host" value={email.host ?? '—'} />
            <Row label="Sender" value={email.senderEmail ?? '—'} />
            <Row
              label="Configured"
              value={email.configured ? 'yes' : 'no'}
              tone={email.configured ? 'ok' : 'bad'}
            />
            <Row label="Sent in window" value={String(email.sentInWindow)} />
            <Row
              label="Failed in window"
              value={String(email.failedInWindow)}
              tone={email.failedInWindow > 0 ? 'bad' : 'ok'}
            />
            <Row label="Last success" value={fmt(email.lastSuccessAt)} />
            {email.lastError && <Row label="Last error" value={email.lastError} tone="bad" />}
          </dl>
        </Section>
      </div>

      {/* OTP funnel */}
      <Section icon={<ShieldAlert className="h-4 w-4" />} title="OTP statistics">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead className="bg-surface-subtle text-ink-subtle font-mono text-[11px] uppercase">
              <tr>
                <th className="border-border/60 border-b px-4 py-2.5 font-bold">Purpose</th>
                <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                  Issued
                </th>
                <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                  Verified
                </th>
                <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                  Expired
                </th>
                <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                  Burned
                </th>
                <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                  Verify rate
                </th>
                <th className="border-border/60 border-b px-4 py-2.5 font-bold">Policy</th>
              </tr>
            </thead>
            <tbody className="divide-border/40 divide-y">
              {data.otp.map((o) => (
                <tr key={o.purpose}>
                  <td className="text-ink px-4 py-2.5 font-semibold capitalize">{o.label}</td>
                  <td className="px-4 py-2.5 text-right">{o.issued}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-600">{o.verified}</td>
                  <td className="text-ink-subtle px-4 py-2.5 text-right">{o.expired}</td>
                  {/* Burned = attempts exhausted. A spike is either an attack or
                      a delivery problem sending people round in circles. */}
                  <td
                    className={`px-4 py-2.5 text-right ${o.burned > 0 ? 'font-bold text-red-500' : 'text-ink-subtle'}`}
                  >
                    {o.burned}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold">
                    {o.verifyRate === null ? '—' : `${o.verifyRate}%`}
                  </td>
                  <td className="text-ink-subtle px-4 py-2.5 text-[12px]">
                    {o.policy.ttlMinutes}m · {o.policy.maxPerHour}/hr · {o.policy.maxAttempts} tries
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Queue health */}
      <Section icon={<Layers className="h-4 w-4" />} title="Queue health">
        {!data.queues.available ? (
          <p className="text-ink-subtle p-5 text-[13px]">{data.queues.reason}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead className="bg-surface-subtle text-ink-subtle font-mono text-[11px] uppercase">
                <tr>
                  <th className="border-border/60 border-b px-4 py-2.5 font-bold">Queue</th>
                  <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                    Waiting
                  </th>
                  <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                    Active
                  </th>
                  <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                    Retrying
                  </th>
                  <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                    Completed
                  </th>
                  <th className="border-border/60 border-b px-4 py-2.5 text-right font-bold">
                    Dead
                  </th>
                  <th className="text-ink-subtle px-4 py-2 text-right font-bold">Oldest waiting</th>
                  <th className="text-ink-subtle px-4 py-2 text-right font-bold">Last processed</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y">
                {data.queues.queues.map((q) => (
                  <tr key={q.name}>
                    <td className="text-ink px-4 py-2.5 font-mono text-[12px] font-semibold">
                      {q.name}
                    </td>
                    <td className="px-4 py-2.5 text-right">{q.waiting}</td>
                    <td className="px-4 py-2.5 text-right">{q.active}</td>
                    <td
                      className={`px-4 py-2.5 text-right ${q.retrying > 0 ? 'font-bold text-amber-600' : ''}`}
                    >
                      {q.retrying}
                    </td>
                    <td className="text-ink-subtle px-4 py-2.5 text-right">{q.completed}</td>
                    <td
                      className={`px-4 py-2.5 text-right ${q.failed > 0 ? 'font-bold text-red-500' : ''}`}
                    >
                      {q.failed}
                    </td>
                    {/* Depth alone cannot tell a draining queue from a stopped
                        one. Age can. */}
                    <td
                      className={`px-4 py-2.5 text-right ${
                        q.staleWaiting > 0 ? 'font-bold text-red-500' : 'text-ink-subtle'
                      }`}
                    >
                      {q.oldestWaitingMs === null ? '—' : formatDelay(q.oldestWaitingMs)}
                      {q.staleWaiting > 0 && (
                        <span className="ml-1 font-medium">
                          ({q.staleWaiting}
                          {/* "+" only once the sample was truncated — jobs exist
                              beyond the ones counted. When the sample covered the
                              whole queue the number is exact, and "3+" for 3 of 3
                              would invent a backlog that is not there. */}
                          {q.staleWaiting >= q.staleSampled && q.staleSampled < q.waiting
                            ? '+'
                            : ''}{' '}
                          stale)
                        </span>
                      )}
                    </td>
                    <td className="text-ink-subtle px-4 py-2.5 text-right">
                      {/* Never "stopped": completed jobs are pruned after 24h or
                          1,000 jobs, whichever comes first, so a quiet night
                          legitimately leaves nothing to read. */}
                      {q.lastCompletedMs === null
                        ? 'no recent activity'
                        : `${formatDelay(q.lastCompletedMs)} ago`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Failures */}
      <Section
        icon={
          totalFailed > 0 ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )
        }
        title={`Recent failures${data.recentFailures.length > 0 ? ` (${data.recentFailures.length})` : ''}`}
      >
        {data.recentFailures.length === 0 ? (
          <p className="text-ink-subtle p-5 text-[13px]">
            No failed deliveries recorded. Recipients are masked when there are.
          </p>
        ) : (
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-left text-[12px]">
              <thead className="bg-surface-subtle text-ink-subtle sticky top-0 font-mono text-[11px] uppercase">
                <tr>
                  <th className="border-border/60 border-b px-4 py-2.5 font-bold">When</th>
                  <th className="border-border/60 border-b px-4 py-2.5 font-bold">Channel</th>
                  <th className="border-border/60 border-b px-4 py-2.5 font-bold">Template</th>
                  <th className="border-border/60 border-b px-4 py-2.5 font-bold">To</th>
                  <th className="border-border/60 border-b px-4 py-2.5 font-bold">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y">
                {data.recentFailures.map((f) => (
                  <tr key={f.id}>
                    <td className="text-ink-subtle whitespace-nowrap px-4 py-2">
                      {new Date(f.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2">{f.channel}</td>
                    <td className="text-ink-subtle px-4 py-2 font-mono">{f.templateKey}</td>
                    <td className="text-ink-subtle px-4 py-2 font-mono">{f.toAddress}</td>
                    <td className="px-4 py-2 text-red-600">{f.error ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

// ── Presentational helpers ───────────────────────────────────────────────────

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
        <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
          {icon}
        </div>
        <h3 className="text-ink text-[14px] font-bold">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Alert({
  tone,
  title,
  detail,
}: {
  tone: 'warning' | 'neutral';
  title: string;
  detail: string;
}) {
  const styles =
    tone === 'warning'
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-border/60 bg-surface-subtle text-ink';
  return (
    <div className={`flex gap-3 rounded-xl border p-4 ${styles}`}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="text-[13px] font-bold">{title}</p>
        <p className="mt-0.5 text-[12.5px] leading-relaxed opacity-90">{detail}</p>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'bad';
}) {
  const colour =
    tone === 'ok'
      ? 'text-emerald-600'
      : tone === 'warn'
        ? 'text-amber-600'
        : tone === 'bad'
          ? 'text-red-500'
          : 'text-ink';
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-ink-subtle shrink-0">{label}</dt>
      <dd className={`text-right font-semibold ${colour}`}>{value}</dd>
    </div>
  );
}

/** Relative time reads faster than a timestamp on an ops screen. */
function fmt(iso: string | null): string {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * A single headline number.
 *
 * Each carries a hint saying what it measures — a bare "p95: 8.4s" means nothing
 * to whoever is woken up by it at 3am.
 */
function Kpi({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'neutral' | 'warning' | 'danger';
}) {
  const colour =
    tone === 'danger' ? 'text-red-500' : tone === 'warning' ? 'text-amber-500' : 'text-ink';
  return (
    <div className="border-border/60 rounded-xl border p-4">
      <p className="text-ink-subtle text-[12px] font-bold uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-2xl font-black ${colour}`}>{value}</p>
      <p className="text-ink-subtle mt-1 text-[12px] font-medium">{hint}</p>
    </div>
  );
}

/** Milliseconds are unreadable past a second or two; scale the unit instead. */
function formatDelay(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 60_000)} min`;
}

/**
 * A few seconds of queue delay is normal; minutes means workers are not keeping
 * up, or are not running at all.
 */
function delayTone(ms: number | null): 'neutral' | 'warning' | 'danger' {
  if (ms === null) return 'neutral';
  if (ms < 5_000) return 'neutral';
  if (ms < 60_000) return 'warning';
  return 'danger';
}
